import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  useMediaQuery,
  Theme,
  Typography,
  AppBar,
  Toolbar,
  styled,
} from '@mui/material';
import { Helmet } from 'react-helmet';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { fetchApiWithoutToken } from '../services/api';

// 인터페이스 정의 추가
interface Itinerary {
  일정명: string;
  내용: string;
  내업체번호: string;
  공유여부: boolean;
  [key: string]: string | number | boolean;
}

interface CityImage {
  도시번호: number;
  도시한글명: string;
  파일경로: string;
}

interface PlaceImage {
  장소번호: number;
  장소한글명: string;
  카피라이팅: string;
  파일경로: string;
  구글맵주소: string;
}

interface CompanyInfo {
  대표자: string;
  사업자번호: string;
  로고경로: string;
  대표전화: string;
  대표이메일: string;
  우편번호: string;
  기본주소: string;
  상세주소: string;
  통신판매업신고번호: string;
  업체명: string;
  업체번호: string;
  담당자: string;
}

interface CustomInfo {
  고객번호: number;
  고객명: string;
  이메일: string;
  전화번호: string;
  메세지: string;
}

interface ApiTableResponse {
  table: Itinerary[];
  table1: CityImage[];
  table2: PlaceImage[];
  table3: CompanyInfo[];
  table4: CustomInfo[];
}

// @media 스타일 제거 함수 추가
const removeMediaQueries = (content: string): string => {
  // @media로 시작하는 모든 스타일 블록을 찾아서 제거
  return content.replace(/@media[^{]+\{([^{}]*\{[^{}]*\})*[^{}]*\}/g, '');
};

const PreviewPage_pc: React.FC = () => {
  const navigate = useNavigate();
  const { itnNo } = useParams();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [itinerary, setItinerary] = useState<Itinerary | null>(null);
  // ... 다른 필요한 state들 추가 ...

  // 모바일 버전으로 이동
  const handleMobileView = () => {
    navigate(`/preview/${itnNo}`);
  };

  // 데이터 fetch 함수
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchApiWithoutToken<ApiTableResponse>('ITN_S_일정표1', [
        { 컬럼명: '@일정번호', 값: itnNo },
      ]);
      
      if (response.data.table?.[0]) {
        setItinerary(response.data.table[0]);
      }
      // ... 다른 데이터 설정 ...
      
    } catch (error) {
      console.error('데이터 로딩 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [itnNo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 일정 내용에서 @media 스타일을 제거한 내용을 반환하는 함수
  const getCleanContent = (content: string | undefined): string => {
    if (!content) return '';
    return removeMediaQueries(content);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>로딩중...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <meta 
          name="viewport" 
          content="width=device-width, initial-scale=1.0, maximum-scale=5.0, minimum-scale=0.1, user-scalable=yes" 
        />
      </Helmet>

      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        WebkitTextSizeAdjust: 'none',
      }}>
        {/* 모바일에서만 보이는 토글 버튼 */}
        {isMobile && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: 1000,
          }}>
            <Button
              variant="outlined"
              startIcon={<PhoneAndroidIcon />}
              onClick={handleMobileView}
              sx={{ 
                width: '180px',
                height: '40px',
                mr: 1,
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }}
            >
              모바일 버전으로 보기
            </Button>
          </Box>
        )}

        {/* 메인 컨텐츠 */}
        <Box sx={{ 
          width: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-x pinch-zoom',
          msOverflowStyle: '-ms-autohiding-scrollbar',
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.2)',
            borderRadius: '4px',
          }
        }}>
          <Container
            maxWidth={false}
            sx={{
              flex: 1,
              width: '1280px',
              minWidth: '1280px',
              margin: '0 auto',
              px: '0 !important',
              py: '16px',
              position: 'relative',
              '@media (max-width: 600px)': {
                transformOrigin: 'top left',
                willChange: 'transform',
              }
            }}>
            {/* 일정 내용 */}
            <Box sx={{ 
              width: '100%',
              '& img': { 
                maxWidth: '100%', 
                height: 'auto',
                touchAction: 'pinch-zoom',
              },
              '& table': { 
                width: '100%',
                touchAction: 'pinch-zoom',
              },
              '& *': { 
                maxWidth: '100%',
                wordBreak: 'break-word',
              }
            }}>
              <div dangerouslySetInnerHTML={{ 
                __html: getCleanContent(itinerary?.내용)
              }} />
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default PreviewPage_pc; 